import { useState, type ReactNode, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Store, LogIn, CheckCircle, Plus, X } from 'lucide-react';
import { api } from '../lib/api';
import { useAuthStore } from '../stores/authStore';

const PRODUCT_CATEGORIES = [
  'Game Keys & Codes',
  'Software & Licenses',
  'Gift Cards',
  'In-Game Items & Currency',
  'Accounts & Subscriptions',
  'Digital Goods & Downloads',
  'E-Books & Guides',
  'Other',
];

const INITIAL_COUNT_OPTIONS = [
  { value: '1-5', label: '1–5 products' },
  { value: '6-20', label: '6–20 products' },
  { value: '21-50', label: '21–50 products' },
  { value: '51-100', label: '51–100 products' },
  { value: '100+', label: '100+ products' },
];

const STOCK_SOURCE_OPTIONS = [
  { value: 'wholesale', label: 'Wholesale / Bulk purchasing' },
  { value: 'publisher', label: 'Direct from publisher / developer' },
  { value: 'self_made', label: 'Self-created / own content' },
  { value: 'reseller', label: 'Authorised reseller programme' },
  { value: 'other', label: 'Other' },
];

export default function SellerApply() {
  const { isAuthenticated, user } = useAuthStore();

  const [form, setForm] = useState({
    storeName: '',
    storeDescription: '',
    productsDescription: '',
    experienceDescription: '',
    stockSource: '',
    stockSourceDetail: '',
    motivation: '',
    initialProductCount: '',
  });
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [suggestedCategories, setSuggestedCategories] = useState<string[]>([]);
  const [suggestInput, setSuggestInput] = useState('');
  const [showSuggestInput, setShowSuggestInput] = useState(false);
  const [portfolioUrls, setPortfolioUrls] = useState<string[]>(['']);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const applyMutation = useMutation({
    mutationFn: (payload: any) => api.post('/sellers/apply', payload),
    onSuccess: () => setSubmitted(true),
    onError: (err: any) => {
      const message = err?.message || 'Something went wrong. Please try again.';
      setErrors({ submit: message });
    },
  });

  // Not logged in
  if (!isAuthenticated) {
    return (
      <div className="py-16 px-4">
        <div className="max-w-lg mx-auto text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gold/20 mb-6">
            <Store className="w-8 h-8 text-gold" />
          </div>
          <h1 className="font-western text-3xl text-gold mb-3">Become a Seller</h1>
          <p className="text-text-secondary mb-8">
            Sign in to apply for a seller account on Outlaw Oasis.
          </p>
          <div className="card p-8">
            <LogIn className="w-10 h-10 text-gold mx-auto mb-4" />
            <h2 className="text-lg font-medium text-cream mb-2">Sign In Required</h2>
            <p className="text-text-secondary text-sm mb-6">
              You need to be logged in to submit a seller application.
            </p>
            <Link
              to="/login?redirect=/sellers/apply"
              className="btn-primary inline-flex items-center gap-2 px-6 py-3"
            >
              Sign In to Continue
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Already a seller
  if (user?.role === 'seller') {
    return (
      <div className="py-16 px-4">
        <div className="max-w-lg mx-auto text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gold/20 mb-6">
            <Store className="w-8 h-8 text-gold" />
          </div>
          <h1 className="font-western text-3xl text-gold mb-3">You're Already a Seller</h1>
          <p className="text-text-secondary mb-8">
            Your seller account is active. Head to your dashboard to manage your store.
          </p>
          <Link to="/dashboard/seller" className="btn-primary inline-flex items-center gap-2 px-6 py-3">
            Go to Seller Dashboard
          </Link>
        </div>
      </div>
    );
  }

  // Success state
  if (submitted) {
    return (
      <div className="py-16 px-4">
        <div className="max-w-lg mx-auto text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-500/20 mb-6">
            <CheckCircle className="w-8 h-8 text-green-400" />
          </div>
          <h1 className="font-western text-3xl text-gold mb-3">Application Submitted!</h1>
          <p className="text-text-secondary mb-2">
            Thank you for applying to become a seller on Outlaw Oasis.
          </p>
          <p className="text-text-secondary mb-8">
            Our team will review your application and get back to you soon.
          </p>
          <Link to="/" className="btn-secondary inline-flex items-center gap-2 px-6 py-3">
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  const toggleCategory = (cat: string) => {
    setSelectedCategories(prev =>
      prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
    );
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (form.storeName.trim().length < 3)
      newErrors.storeName = 'Store name must be at least 3 characters.';
    if (form.storeDescription.trim().length < 50)
      newErrors.storeDescription = 'Please write at least 50 characters describing your store.';
    if (form.productsDescription.trim().length < 20)
      newErrors.productsDescription = 'Please write at least 20 characters about your products.';
    if (!form.stockSource)
      newErrors.stockSource = 'Please select how you source your stock.';
    if (form.stockSourceDetail.trim().length < 20)
      newErrors.stockSourceDetail = 'Please provide at least 20 characters of detail about your stock source.';
    if (selectedCategories.length === 0 && suggestedCategories.length === 0)
      newErrors.categories = 'Please select or suggest at least one product category.';
    if (!form.initialProductCount)
      newErrors.initialProductCount = 'Please select how many products you plan to list.';
    const validUrls = portfolioUrls.filter(u => u.trim());
    for (const url of validUrls) {
      try { new URL(url); } catch {
        newErrors.portfolioUrls = 'One or more portfolio URLs are invalid.';
        break;
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    applyMutation.mutate({
      storeName: form.storeName.trim(),
      storeDescription: form.storeDescription.trim(),
      productsDescription: form.productsDescription.trim(),
      experienceDescription: form.experienceDescription.trim() || undefined,
      portfolioUrls: portfolioUrls.filter(u => u.trim()),
      applicationData: {
        stockSource: form.stockSource,
        stockSourceDetail: form.stockSourceDetail.trim(),
        categories: selectedCategories,
        suggestedCategories: suggestedCategories.length > 0 ? suggestedCategories : undefined,
        initialProductCount: form.initialProductCount,
        motivation: form.motivation.trim() || undefined,
      },
    });
  };

  const addUrlField = () => {
    if (portfolioUrls.length < 5) setPortfolioUrls([...portfolioUrls, '']);
  };

  const removeUrlField = (index: number) => {
    setPortfolioUrls(portfolioUrls.filter((_, i) => i !== index));
  };

  const updateUrl = (index: number, value: string) => {
    const updated = [...portfolioUrls];
    updated[index] = value;
    setPortfolioUrls(updated);
  };

  const Field = ({ label, required, hint, children, error }: {
    label: string;
    required?: boolean;
    hint?: string;
    children: ReactNode;
    error?: string;
  }) => (
    <div>
      <label className="block text-sm font-medium text-cream mb-1">
        {label}
        {required ? (
          <span className="text-hot ml-1">*</span>
        ) : (
          <span className="text-text-muted text-xs font-normal ml-1">(optional)</span>
        )}
      </label>
      {hint && <p className="text-text-muted text-xs mb-2">{hint}</p>}
      {children}
      {error && <p className="text-hot text-xs mt-1">{error}</p>}
    </div>
  );

  return (
    <div className="py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gold/20 mb-4">
            <Store className="w-7 h-7 text-gold" />
          </div>
          <h1 className="font-western text-4xl text-gold mb-2">Become a Seller</h1>
          <p className="text-text-secondary">
            Answer a few questions so we can review your application.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">

          {/* ── Section 1: Your Store ── */}
          <div className="card p-6 space-y-5">
            <h2 className="font-western text-xl text-gold border-b border-gold/20 pb-3">Your Store</h2>

            <Field label="Store Name" required error={errors.storeName}>
              <input
                type="text"
                className="input"
                placeholder="e.g. Dusty Trail Digital"
                value={form.storeName}
                onChange={e => setForm({ ...form, storeName: e.target.value })}
              />
            </Field>

            <Field
              label="About Your Store"
              required
              hint="Tell us what your store is about, your brand, and what makes you unique."
              error={errors.storeDescription}
            >
              <textarea
                className="input min-h-[100px] resize-y"
                placeholder="Describe your store in at least 50 characters..."
                value={form.storeDescription}
                onChange={e => setForm({ ...form, storeDescription: e.target.value })}
              />
              <p className="text-text-muted text-xs mt-1 text-right">{form.storeDescription.length} / 1000</p>
            </Field>
          </div>

          {/* ── Section 2: Your Products ── */}
          <div className="card p-6 space-y-5">
            <h2 className="font-western text-xl text-gold border-b border-gold/20 pb-3">Your Products</h2>

            <Field
              label="What will you sell?"
              required
              hint="Be specific — e.g. 'Steam game keys for AAA titles, focusing on new releases.'"
              error={errors.productsDescription}
            >
              <textarea
                className="input min-h-[100px] resize-y"
                placeholder="Describe the products you plan to sell..."
                value={form.productsDescription}
                onChange={e => setForm({ ...form, productsDescription: e.target.value })}
              />
              <p className="text-text-muted text-xs mt-1 text-right">{form.productsDescription.length} / 1000</p>
            </Field>

            <Field
              label="Which categories will you sell in?"
              required
              error={errors.categories}
            >
              <div className="flex flex-wrap gap-2 mt-1">
                {PRODUCT_CATEGORIES.map(cat => (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => toggleCategory(cat)}
                    className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${
                      selectedCategories.includes(cat)
                        ? 'bg-gold/20 border-gold/50 text-gold'
                        : 'bg-leather border-gold/20 text-text-secondary hover:border-gold/40 hover:text-cream'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>

              {/* Suggested categories chips */}
              {suggestedCategories.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {suggestedCategories.map(cat => (
                    <span
                      key={cat}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border border-dashed border-amber-500/50 bg-amber-500/10 text-amber-400"
                    >
                      <span>💡</span>
                      {cat}
                      <button
                        type="button"
                        onClick={() => setSuggestedCategories(prev => prev.filter(c => c !== cat))}
                        className="hover:text-hot transition-colors ml-0.5"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {/* Suggest input */}
              {showSuggestInput ? (
                <div className="flex gap-2 mt-2">
                  <input
                    type="text"
                    className="input flex-1 text-sm"
                    placeholder="e.g. Trading Cards, NFTs..."
                    value={suggestInput}
                    maxLength={60}
                    onChange={e => setSuggestInput(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const val = suggestInput.trim();
                        if (val && !suggestedCategories.includes(val) && suggestedCategories.length < 5) {
                          setSuggestedCategories(prev => [...prev, val]);
                          setSuggestInput('');
                          setShowSuggestInput(false);
                        }
                      }
                      if (e.key === 'Escape') {
                        setSuggestInput('');
                        setShowSuggestInput(false);
                      }
                    }}
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const val = suggestInput.trim();
                      if (val && !suggestedCategories.includes(val) && suggestedCategories.length < 5) {
                        setSuggestedCategories(prev => [...prev, val]);
                        setSuggestInput('');
                        setShowSuggestInput(false);
                      }
                    }}
                    className="px-3 py-1.5 text-sm bg-amber-500/20 text-amber-400 border border-amber-500/30 rounded-lg hover:bg-amber-500/30 transition-colors"
                  >
                    Add
                  </button>
                  <button
                    type="button"
                    onClick={() => { setSuggestInput(''); setShowSuggestInput(false); }}
                    className="p-2 text-text-muted hover:text-cream transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : suggestedCategories.length < 5 && (
                <button
                  type="button"
                  onClick={() => setShowSuggestInput(true)}
                  className="mt-2 text-sm text-amber-400 hover:text-amber-300 flex items-center gap-1 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Suggest a category not listed
                </button>
              )}
            </Field>

            <Field
              label="How many products do you plan to list initially?"
              required
              error={errors.initialProductCount}
            >
              <div className="flex flex-wrap gap-2 mt-1">
                {INITIAL_COUNT_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setForm({ ...form, initialProductCount: opt.value })}
                    className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${
                      form.initialProductCount === opt.value
                        ? 'bg-gold/20 border-gold/50 text-gold'
                        : 'bg-leather border-gold/20 text-text-secondary hover:border-gold/40 hover:text-cream'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </Field>
          </div>

          {/* ── Section 3: Your Stock ── */}
          <div className="card p-6 space-y-5">
            <h2 className="font-western text-xl text-gold border-b border-gold/20 pb-3">Stock & Sourcing</h2>

            <Field
              label="How do you source your stock?"
              required
              error={errors.stockSource}
            >
              <div className="flex flex-col gap-2 mt-1">
                {STOCK_SOURCE_OPTIONS.map(opt => (
                  <label
                    key={opt.value}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg border cursor-pointer transition-colors ${
                      form.stockSource === opt.value
                        ? 'bg-gold/10 border-gold/50 text-cream'
                        : 'bg-leather border-gold/20 text-text-secondary hover:border-gold/40'
                    }`}
                  >
                    <input
                      type="radio"
                      name="stockSource"
                      value={opt.value}
                      checked={form.stockSource === opt.value}
                      onChange={e => setForm({ ...form, stockSource: e.target.value })}
                      className="accent-gold"
                    />
                    <span className="text-sm">{opt.label}</span>
                  </label>
                ))}
              </div>
            </Field>

            <Field
              label="Tell us more about how you get your stock"
              required
              hint="E.g. which suppliers, how you verify legitimacy, how you maintain consistent supply."
              error={errors.stockSourceDetail}
            >
              <textarea
                className="input min-h-[100px] resize-y"
                placeholder="Provide details about your supply chain or sourcing process..."
                value={form.stockSourceDetail}
                onChange={e => setForm({ ...form, stockSourceDetail: e.target.value })}
              />
              <p className="text-text-muted text-xs mt-1 text-right">{form.stockSourceDetail.length} / 2000</p>
            </Field>
          </div>

          {/* ── Section 4: Background ── */}
          <div className="card p-6 space-y-5">
            <h2 className="font-western text-xl text-gold border-b border-gold/20 pb-3">Background</h2>

            <Field
              label="Prior selling experience"
              hint="Have you sold on other marketplaces? Any relevant background or references?"
            >
              <textarea
                className="input min-h-[80px] resize-y"
                placeholder="e.g. 'Sold on G2A for 2 years with 500+ positive reviews...'"
                value={form.experienceDescription}
                onChange={e => setForm({ ...form, experienceDescription: e.target.value })}
              />
            </Field>

            <Field
              label="Why do you want to sell on Outlaw Oasis?"
              hint="What makes Outlaw Oasis the right platform for you?"
            >
              <textarea
                className="input min-h-[80px] resize-y"
                placeholder="Tell us what draws you to our marketplace..."
                value={form.motivation}
                onChange={e => setForm({ ...form, motivation: e.target.value })}
              />
            </Field>

            <Field
              label="Portfolio / Reference Links"
              hint="Links to your existing store, social proof, or any relevant references. Up to 5."
              error={errors.portfolioUrls}
            >
              <div className="space-y-2">
                {portfolioUrls.map((url, index) => (
                  <div key={index} className="flex gap-2">
                    <input
                      type="url"
                      className="input flex-1"
                      placeholder="https://example.com"
                      value={url}
                      onChange={e => updateUrl(index, e.target.value)}
                    />
                    {portfolioUrls.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeUrlField(index)}
                        className="p-2 text-text-muted hover:text-hot transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {portfolioUrls.length < 5 && (
                <button
                  type="button"
                  onClick={addUrlField}
                  className="mt-2 text-sm text-gold hover:text-gold-bright flex items-center gap-1 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Add another URL
                </button>
              )}
            </Field>
          </div>

          {/* Submit error */}
          {errors.submit && (
            <div className="p-3 rounded bg-hot/10 border border-hot/30 text-hot text-sm">
              {errors.submit}
            </div>
          )}

          {/* Submit */}
          <div className="card p-6">
            <button
              type="submit"
              disabled={applyMutation.isPending}
              className="btn-primary w-full py-3 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {applyMutation.isPending ? 'Submitting...' : 'Submit Application'}
            </button>
            <p className="text-text-muted text-xs text-center mt-3">
              Applications are typically reviewed within 1–3 business days.
            </p>
          </div>

        </form>
      </div>
    </div>
  );
}
